import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import useFiltroLembrado from '../hooks/useFiltroLembrado';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';
import '../styles/fiscal.css';
import '../styles/fiscalModerno.css';

// Fiscal → Regime de Apuração do Simples Nacional (Integra Contador). Espelho, por empresa
// do Simples, do regime de receitas (CAIXA × COMPETÊNCIA) de cada ano. Só leitura na API —
// a opção do ano seguinte continua sendo feita no portal; aqui o controle de quem falta.

const API = process.env.REACT_APP_API_BASE;
const URL = `${API}/api/fiscal/regime`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const semAcento = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const fmtCnpj = (c) => (c && c.length === 14 ? c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : c || '—');

function SeloRegime({ regime, semOpcao }) {
  if (regime === 'CAIXA') return <span className="fz-selo alerta"><i className="fas fa-hand-holding-dollar"></i>Caixa</span>;
  if (regime) return <span className="fz-selo ok"><i className="fas fa-file-invoice"></i>Competência</span>;
  return semOpcao
    ? <span className="fz-selo erro"><i className="fas fa-circle-exclamation"></i>sem opção</span>
    : <span className="fz-selo neutro"><i className="fas fa-minus"></i>—</span>;
}

export default function FiscalRegime() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  // filtros lembrados: trocar de tela e voltar mantém a seleção
  const [busca, setBusca] = useFiltroLembrado('fiscalRegime.busca', '');
  const [fAtual, setFAtual] = useFiltroLembrado('fiscalRegime.atual', '');
  const [soSemOpcao, setSoSemOpcao] = useFiltroLembrado('fiscalRegime.semOpcao', false);
  const [sync, setSync] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [detalhe, setDetalhe] = useState(null);      // { item, historico aberto }
  const [baixando, setBaixando] = useState('');
  const montado = useRef(true);
  useEffect(() => { montado.current = true; return () => { montado.current = false; }; }, []);

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${URL}/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => { setDados(r.data); setErro(''); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar os regimes'))
      .finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  // Sincronização em blocos: chama de novo até o backend dizer "concluido".
  const sincronizar = async () => {
    setSincronizando(true); setErro(''); setMsg('');
    let reiniciar = true;
    try {
      for (;;) {
        const r = await axios.post(`${URL}/sincronizar/`, { reiniciar }, { headers: headers() });
        reiniciar = false;
        if (!montado.current) return;
        setSync(r.data.sync);
        if (r.data.sync.concluido) {
          const s = r.data.sync;
          setMsg(`Sincronizado: ${s.total} empresa(s) do Simples${s.erros?.length ? ` · ${s.erros.length} com erro` : ''}`);
          break;
        }
      }
      carregar();
    } catch (e) {
      if (montado.current) setErro(e.response?.data?.mensagem || 'Não foi possível sincronizar');
    } finally {
      if (montado.current) setSincronizando(false);
    }
  };

  const comprovante = async (item, ano) => {
    setBaixando(`${item.cod}-${ano}`); setErro('');
    try {
      const r = await axios.get(`${URL}/${item.cod}/comprovante/`, { params: { ano, pdf: 1, _: Date.now() }, headers: headers(), responseType: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(r.data);
      link.download = `regime-apuracao-${item.cod}-${ano}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (e) {
      try {
        const corpo = JSON.parse(await e.response.data.text());
        setErro(corpo.mensagem || 'Não foi possível baixar o comprovante');
      } catch { setErro('Não foi possível baixar o comprovante'); }
    } finally {
      setBaixando('');
    }
  };

  const itens = useMemo(() => dados?.itens || [], [dados]);
  const anoAtual = dados?.ano_atual || new Date().getFullYear();
  const anoSeguinte = dados?.ano_seguinte || anoAtual + 1;

  const lista = useMemo(() => {
    const termo = semAcento(busca.trim());
    const digitos = busca.replace(/\D/g, '');
    return itens.filter((i) => {
      if (fAtual === 'sem' ? i.atual : (fAtual && i.atual !== fAtual)) return false;
      if (soSemOpcao && i.seguinte) return false;
      if (!termo) return true;
      return String(i.cod) === busca.trim() || semAcento(i.cliente).includes(termo)
        || (digitos.length >= 4 && (i.cnpj || '').includes(digitos));
    });
  }, [itens, busca, fAtual, soSemOpcao]);

  const kpis = useMemo(() => ([
    { rotulo: `Sem opção ${anoSeguinte}`, valor: itens.filter((i) => !i.seguinte).length, icone: 'fa-circle-exclamation', cor: 'c-bad' },
    { rotulo: `Caixa em ${anoAtual}`, valor: itens.filter((i) => i.atual === 'CAIXA').length, icone: 'fa-hand-holding-dollar', cor: 'c-warn' },
    { rotulo: `Competência em ${anoAtual}`, valor: itens.filter((i) => i.atual === 'COMPETENCIA').length, icone: 'fa-file-invoice', cor: 'c-ok' },
    { rotulo: `Sem opção ${anoAtual}`, valor: itens.filter((i) => !i.atual).length, icone: 'fa-question', cor: 'c-warn' },
    { rotulo: 'Empresas do Simples', valor: itens.length, icone: 'fa-building', cor: 'c-navy' },
  ]), [itens, anoAtual, anoSeguinte]);

  const pct = sync && sync.total ? Math.round((sync.indice / sync.total) * 100) : 0;
  const ultimo = sync || dados?.sync;

  return (
    <div className="painel-op fz-app">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-scale-balanced header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">Regime de Apuração (Simples)</h2>
          </div>
          <div className="gr-toolbar">
            <button type="button" className="gr-btn" onClick={carregar} disabled={carregando || sincronizando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
            </button>
            <button type="button" className="gr-btn primaria" onClick={sincronizar} disabled={sincronizando}>
              <i className={`fas ${sincronizando ? 'fa-spinner fa-spin' : 'fa-cloud-arrow-down'}`} aria-hidden="true"></i>
              {sincronizando ? `Sincronizando ${pct}%` : 'Sincronizar com a Receita'}
            </button>
          </div>
        </div>

        {dados?.janela_aberta && (
          <div className="alert alert-warning py-2">
            <i className="fas fa-bell me-2"></i><b>Janela de opção aberta:</b> a escolha do regime de {anoSeguinte} é feita
            agora no portal do Simples Nacional — as empresas "sem opção {anoSeguinte}" abaixo ainda não optaram.
          </div>
        )}
        {sincronizando && sync && sync.total != null && (
          <div className="mb-3">
            <div className="progress" style={{ height: 6 }}><div className="progress-bar" style={{ width: `${pct}%`, background: '#00b5cc' }}></div></div>
            <div className="fz-sub mt-1">{sync.indice} de {sync.total} empresas lidas · só leitura no Integra Contador</div>
          </div>
        )}
        {!sincronizando && (
          <div className="fz-sub mb-2">
            <i className="fas fa-circle-info me-1"></i>
            {ultimo?.concluido_em
              ? <>Última sincronização: {ultimo.concluido_em}{ultimo.erros?.length ? ` · ${ultimo.erros.length} empresa(s) com erro` : ''}.</>
              : ultimo && !ultimo.concluido
                ? <>Sincronização incompleta ({ultimo.indice}/{ultimo.total}). Clique em "Sincronizar" para concluir.</>
                : <>Ainda não sincronizado — clique em "Sincronizar com a Receita".</>}
            {' '}Regime de apuração de receitas (Caixa × Competência) por ano; a opção é renovada todo fim de ano no portal.
          </div>
        )}
        {erro && <div className="alert alert-danger py-2" role="button" onClick={() => setErro('')}><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}
        {msg && <div className="alert alert-success py-2" role="button" onClick={() => setMsg('')}><i className="fas fa-circle-check me-2"></i>{msg}</div>}
        {!sincronizando && ultimo?.erros?.length > 0 && (
          <details className="small mb-2"><summary className="text-danger">Empresas com erro na sincronização</summary>
            <ul className="mb-0">{ultimo.erros.map((e) => <li key={e}>{e}</li>)}</ul>
          </details>
        )}

        <div className="po-kpis" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
          {kpis.map((k) => (
            <div key={k.rotulo} className={`po-kpi ${k.cor} ${k.valor ? '' : 'po-zero'}`}>
              <div className="po-kpi-rotulo"><i className={`fas ${k.icone}`} aria-hidden="true"></i>{k.rotulo}</div>
              <div className="po-kpi-valor">{k.valor}</div>
            </div>
          ))}
        </div>

        <div className="fz-filtros">
          <input className="po-busca" placeholder="Buscar por nome, código ou CNPJ" value={busca} onChange={(e) => setBusca(e.target.value)} />
          <select className="fz-sel" value={fAtual} onChange={(e) => setFAtual(e.target.value)} aria-label={`Regime em ${anoAtual}`}>
            <option value="">{`Regime ${anoAtual}: todos`}</option>
            <option value="CAIXA">Caixa</option>
            <option value="COMPETENCIA">Competência</option>
            <option value="sem">Sem opção</option>
          </select>
          <label className="po-toggle"><input type="checkbox" checked={soSemOpcao} onChange={(e) => setSoSemOpcao(e.target.checked)} />Só sem opção {anoSeguinte}</label>
        </div>

        <section className="po-card po-enter fz-topo-fixo">
          <header className="po-card-head">
            <h3><i className="fas fa-scale-balanced" aria-hidden="true"></i>Empresas do Simples</h3>
            <span className="badge bg-light text-dark">{lista.length}</span>
          </header>
          <div className="po-card-body">
            {/* sem rolagem interna: a lista rola com a página e o cabeçalho fica fixo no topo */}
            <div style={{ minHeight: 380 }}>
              <table className="table table-hover po-table align-middle mb-0">
                <thead><tr><th>Empresa</th><th>{`Regime ${anoAtual}`}</th><th>{`Regime ${anoSeguinte}`}</th><th>Sincronizado</th><th></th></tr></thead>
                <tbody>
                  {lista.map((i) => (
                    <tr key={i.cod} style={!i.seguinte && i.sincronizado_em ? { background: '#fff9f0' } : undefined}>
                      <td>
                        <div className="fw-semibold">{i.cliente}</div>
                        <div className="fz-sub fz-mono">{i.cod} · {fmtCnpj(i.cnpj)}</div>
                      </td>
                      <td><SeloRegime regime={i.atual} semOpcao={!!i.sincronizado_em} /></td>
                      <td><SeloRegime regime={i.seguinte} semOpcao={!!i.sincronizado_em} /></td>
                      <td className="small">{i.sincronizado_em || <span className="text-muted">nunca</span>}</td>
                      <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                        {i.historico.length > 0 && (
                          <button className="btn btn-sm btn-outline-secondary me-1" title="Histórico por ano" onClick={() => setDetalhe(i)}>
                            <i className="fas fa-clock-rotate-left"></i>
                          </button>
                        )}
                        {i.atual && (
                          <button className="btn btn-sm btn-outline-primary" title={`Baixar o comprovante da opção de ${anoAtual} (PDF da Receita)`}
                            onClick={() => comprovante(i, anoAtual)} disabled={!!baixando}>
                            <i className={`fas ${baixando === `${i.cod}-${anoAtual}` ? 'fa-spinner fa-spin' : 'fa-file-pdf'}`}></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {lista.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-4">{itens.length ? 'Nenhuma empresa com esses filtros.' : 'Nenhuma empresa do Simples sincronizada — clique em "Sincronizar com a Receita".'}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {detalhe && (
          <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-clock-rotate-left me-2" style={{ color: '#00b5cc' }}></i>Histórico do regime</h5>
                    <small style={{ color: '#6c757d' }}>{detalhe.cod} · {detalhe.cliente}</small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setDetalhe(null)}></button>
                </div>
                <div className="modal-body">
                  <table className="table table-sm po-table align-middle mb-0">
                    <thead><tr><th>Ano</th><th>Regime</th><th className="text-end">Comprovante</th></tr></thead>
                    <tbody>
                      {detalhe.historico.map((h) => (
                        <tr key={h.ano}>
                          <td className="fz-num">{h.ano}</td>
                          <td><SeloRegime regime={h.regime} semOpcao /></td>
                          <td className="text-end">
                            <button className="btn btn-sm btn-outline-primary" onClick={() => comprovante(detalhe, h.ano)} disabled={!!baixando} title="PDF da Receita">
                              <i className={`fas ${baixando === `${detalhe.cod}-${h.ano}` ? 'fa-spinner fa-spin' : 'fa-file-pdf'}`}></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="modal-footer bg-light">
                  <button className="btn btn-secondary" onClick={() => setDetalhe(null)}><i className="fas fa-times me-2"></i>Fechar</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
